import type { RProvider, RProviders } from 'v2/pages/flow/sidebar/types'
import type { Node } from '@xyflow/react'
import type { AiSkillsNodeData } from '../types'
import { DEFAULT_TEXT_SUMMARIZATION_INSTRUCTIONS } from '../constants'

// Helper function to get default provider and model for each config type
const getDefaultProviderAndModel = (providers?: RProviders) => {
  const getDefaultProvider = (providerType: keyof RProviders) => {
    const providerList = providers?.[providerType]
    return providerList?.find((p) => p.default) || providerList?.[0]
  }

  const getDefaultModel = (provider: RProvider | undefined) =>
    provider?.models?.find((m) => m.default)?.id || provider?.models?.[0]?.id
  // Providers
  const imageGenProvider = getDefaultProvider('image_gen_providers')
  const imageAnalysisProvider = getDefaultProvider('image_analysis_providers')
  const sttProvider = getDefaultProvider('stt_providers')
  const ttsProvider = getDefaultProvider('tts_providers')
  const llmProvider = getDefaultProvider('llm_providers')

  return {
    image_generation: {
      provider: imageGenProvider,
      model: getDefaultModel(imageGenProvider),
    },
    image_analysis: {
      provider: imageAnalysisProvider,
      model: getDefaultModel(imageAnalysisProvider),
    },
    audio_transcription: {
      provider: sttProvider,
      model: getDefaultModel(sttProvider),
    },
    text_to_speech: {
      provider: ttsProvider,
      model: getDefaultModel(ttsProvider),
    },
    text_summarization: {
      provider: llmProvider,
      model: getDefaultModel(llmProvider),
    },
  }
}

// Helper function to create default values for all configs
export const createDefaultConfigs = (
  selectedNode: Node<AiSkillsNodeData>,
  providers?: RProviders,
) => {
  const defaults = getDefaultProviderAndModel(providers)
  const hasSavedToolType = selectedNode.data.config.tool_type

  return {
    tool_type: hasSavedToolType || 'image_generation',
    image_generation_config: {
      model_id:
        selectedNode.data.config.image_generation_config?.model_id ||
        defaults.image_generation.model ||
        '',
      provider_id:
        selectedNode.data.config.image_generation_config?.provider_id ||
        defaults.image_generation.provider?.id ||
        '',
      instructions: selectedNode.data.config.image_generation_config?.instructions || '',
    },
    image_analysis_config: {
      model_id:
        selectedNode.data.config.image_analysis_config?.model_id ||
        defaults.image_analysis.model ||
        '',
      provider_id:
        selectedNode.data.config.image_analysis_config?.provider_id ||
        defaults.image_analysis.provider?.id ||
        '',
      img_url: selectedNode.data.config.image_analysis_config?.img_url || '',
      instructions: selectedNode.data.config.image_analysis_config?.instructions || '',
    },
    audio_transcription_config: {
      model_id:
        selectedNode.data.config.audio_transcription_config?.model_id ||
        defaults.audio_transcription.model ||
        '',
      provider_id:
        selectedNode.data.config.audio_transcription_config?.provider_id ||
        defaults.audio_transcription.provider?.id ||
        '',
      language: selectedNode.data.config.audio_transcription_config?.language || 'en',
      audio_url: selectedNode.data.config.audio_transcription_config?.audio_url || '',
    },
    text_to_speech_config: {
      model_id:
        selectedNode.data.config.text_to_speech_config?.model_id ||
        defaults.text_to_speech.model ||
        '',
      provider_id:
        selectedNode.data.config.text_to_speech_config?.provider_id ||
        defaults.text_to_speech.provider?.id ||
        '',
      voice_id:
        selectedNode.data.config.text_to_speech_config?.voice_id ||
        defaults.text_to_speech.provider?.voices?.[0]?.id ||
        '',
      instructions: selectedNode.data.config.text_to_speech_config?.instructions || '',
      text: selectedNode.data.config.text_to_speech_config?.text || '',
    },
    summary_config: {
      model_id:
        selectedNode.data.config.summary_config?.model_id ||
        defaults.text_summarization.model ||
        '',
      provider_id:
        selectedNode.data.config.summary_config?.provider_id ||
        defaults.text_summarization.provider?.id ||
        '',
      summary_url: selectedNode.data.config.summary_config?.summary_url || '',
      instructions:
        selectedNode.data.config.summary_config?.instructions ||
        DEFAULT_TEXT_SUMMARIZATION_INSTRUCTIONS,
    },
  }
}

// Callback to filter data based on tool type
export const handleSubmit = (data: AiSkillsNodeData['config']) => {
  const { tool_type: toolType } = data

  // Create base config with common fields
  const filteredConfig = {
    name: data.name,
    tool_type: toolType,
  }

  // Add only the config for the selected tool type
  switch (toolType) {
    case 'image_generation':
      return {
        ...filteredConfig,
        image_generation_config: data.image_generation_config,
      }
    case 'image_analysis':
      return {
        ...filteredConfig,
        image_analysis_config: data.image_analysis_config,
      }
    case 'audio_transcription':
      return {
        ...filteredConfig,
        audio_transcription_config: data.audio_transcription_config,
      }
    case 'text_to_speech':
      return {
        ...filteredConfig,
        text_to_speech_config: data.text_to_speech_config,
      }
    case 'summary':
      return {
        ...filteredConfig,
        summary_config: data.summary_config,
      }
    default:
      return data
  }
}
